import type { ActivityPair } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActivityType,
  BolusState,
  CGMType,
  ExerciseIntensity,
  PumpSuspendReason,
  PumpSuspendState,
} from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import { forwardRef } from "react"

import type {
  ReadOnlySubEntry,
  ReadOnlyUserEvent,
} from "src/types"

export type Props = ComponentPropsWithoutRef<"div"> &
  ActivityPair & {
    item: ReadOnlySubEntry | ReadOnlyUserEvent;
  }

/*
 * this component returns null if there is not description
 * it relies on parity with the ActivityMessage and ActivityNoteIcon component
 */
export const ActivityDescription = forwardRef<HTMLDivElement, Props>(
  function ActivityDescription(props: Props, ref: Ref<HTMLDivElement>) {
    const {
      activity, activityType, item, ...htmlProps
    } = props

    if (activityType === ActivityType.Note) {
      return null
    }

    if (activityType === ActivityType.Exercise) {
      const userEvent = item as ReadOnlyUserEvent
      const subEntry = item as ReadOnlySubEntry

      const hasDuration =
        subEntry.duration_in_seconds ||
        userEvent.ended_at !== userEvent.started_at

      // this matches the criteria in ExerciseActivityMessage
      if (
        !hasDuration &&
        activity.intensity === ExerciseIntensity.None &&
        !item.note
      ) {
        return null
      }
    } else if (activityType === ActivityType.Bolus) {
      if (activity.state !== BolusState.InProgress) {
        return null
      }
    } else if (activityType === ActivityType.PumpSuspension) {
      // this matches the criteria in PumpSuspensionActivityMessage
      const hasMessage = (
        activity.state === PumpSuspendState.Completed ||
        activity.suspend_reason === PumpSuspendReason.Alarm ||
        activity.suspend_reason === PumpSuspendReason.PumpMalfunction ||
        activity.suspend_reason === PumpSuspendReason.UserAborted
      )

      if (!hasMessage) {
        return null
      }
    } else if (activityType === ActivityType.CGMSessionEnded) {
      if (activity.cgm_type === CGMType.Undefined) {
        return null
      }
    } else if (!item.note) {
      return null
    }

    return (
      <div
        {...htmlProps}
        ref={ref}
      />
    )
  },
)
